import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/useApp.js';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { userService, lawyerService } from '../services/firebaseService';
import toast from 'react-hot-toast';
import logoSvg from '../assets/icons/logo.svg';
import { Eye, EyeOff, X } from 'lucide-react';

const AuthPage = () => {
  console.log('AuthPage component rendering...'); // Debug log
  
  const navigate = useNavigate();
  
  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY AT THE TOP LEVEL
  const [authMethod, setAuthMethod] = useState('signup');
  const [showPassword, setShowPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  });
  
  // Context hooks
  const appContext = useApp();
  const { user: currentUser, signIn, signUp, signInWithGoogle, signInWithGoogleRedirect, resetPassword, logout: firebaseLogout, loading: authLoading } = useFirebaseAuth();
  
  // Safe access to context values with defaults
  const language = appContext?.language || 'english';
  const isAuthenticated = Boolean(appContext?.isAuthenticated);
  const userRole = appContext?.userRole || null;
  const isProfileComplete = Boolean(appContext?.isProfileComplete);
  
  // Handle navigation after successful authentication
  useEffect(() => {
    console.log('🔍 AuthPage Navigation useEffect triggered:', {
      authLoading,
      isAuthenticated,
      userRole,
      isProfileComplete,
      currentUser: currentUser?.uid
    });
    
    if (authLoading) {
      console.log('⏳ Auth is still loading, waiting...');
      return; // Don't navigate while auth is loading
    }
    
    // Auto-navigate authenticated users to appropriate dashboard
    if (isAuthenticated && currentUser?.uid && !justLoggedIn) {
      console.log('🔄 User already authenticated, checking role and redirecting...');
      setIsRedirecting(true);
      // Add a small delay to ensure user data is properly loaded
      setTimeout(() => {
        handlePostAuthNavigation(currentUser);
      }, 1000);
    }
  }, [isAuthenticated, userRole, isProfileComplete, navigate, authLoading, currentUser, justLoggedIn]);

  // Handle post-authentication navigation based on user role and profile status
  const handlePostAuthNavigation = async (user = null) => {
    const targetUser = user || currentUser;
    
    if (!targetUser?.uid) {
      console.error("❌ No user UID found, redirecting to role selection");
      return navigate("/role-selection");
    }

    console.log("🔍 Starting post-auth navigation for user:", targetUser.uid);

    try {
      // Fetch base user profile with retry mechanism
      let userProfile = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (!userProfile && retryCount < maxRetries) {
        const profileResult = await userService.getUserProfile(targetUser.uid);
        if (profileResult && profileResult.success && profileResult.data) {
          userProfile = profileResult.data;
          console.log("✅ User profile found:", userProfile);
        } else {
          console.log(`🔄 User profile not found, retry ${retryCount + 1}/${maxRetries}`);
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
          retryCount++;
        }
      }

      if (!userProfile || !userProfile.role) {
        console.log("❓ No role found → redirecting to role selection");
        return navigate("/role-selection");
      }

      if (userProfile.role === "lawyer") {
        console.log("👨‍⚖️ User is a lawyer, checking lawyer profile...");
        // Get lawyer profile
        const lawyerProfile = await lawyerService.getLawyerByUserId(targetUser.uid);
        console.log("👨‍⚖️ Lawyer profile result:", lawyerProfile);

        if (lawyerProfile?.success && lawyerProfile?.data) {
          console.log("✅ Lawyer profile found:", lawyerProfile.data);
          // Check if lawyer has essential profile data (name, specialization, etc.)
          const hasEssentialData = lawyerProfile.data.full_name || lawyerProfile.data.fullName;
          const isVerified = lawyerProfile.data.verificationStatus === 'approved' || lawyerProfile.data.isVerified;
          
          console.log("🔍 Lawyer profile analysis:", {
            hasEssentialData,
            isVerified,
            verificationStatus: lawyerProfile.data.verificationStatus
          });
          
          if (hasEssentialData) {
            if (isVerified) {
              console.log("✅ Lawyer complete + verified → dashboard");
              return navigate("/lawyer-dashboard");
            } else {
              console.log("⏳ Lawyer complete + pending → approval page");
              // Clear any loading states and redirect immediately
              setIsRedirecting(false);
              return navigate("/pending-approval");
            }
          } else {
            console.log("📝 Lawyer profile incomplete → profile page");
            return navigate("/lawyer-profile");
          }
        } else {
          console.log("📝 No lawyer profile found → create profile page");
          return navigate("/lawyer-profile");
        }
      }

      // Additional check: If user doesn't have a role but has a lawyer profile, treat them as a lawyer
      if (!userProfile.role) {
        console.log("🔍 No role set, checking if user has lawyer profile...");
        const lawyerProfile = await lawyerService.getLawyerByUserId(targetUser.uid);
        
        if (lawyerProfile?.success && lawyerProfile?.data) {
          console.log("✅ Found lawyer profile for user without role, treating as lawyer");
          const hasEssentialData = lawyerProfile.data.full_name || lawyerProfile.data.fullName;
          const isVerified = lawyerProfile.data.verificationStatus === 'approved' || lawyerProfile.data.isVerified;
          
          if (hasEssentialData) {
            if (isVerified) {
              console.log("✅ Lawyer complete + verified → dashboard");
              return navigate("/lawyer-dashboard");
            } else {
              console.log("⏳ Lawyer complete + pending → approval page");
              return navigate("/pending-approval");
            }
          } else {
            console.log("📝 Lawyer profile incomplete → profile page");
            return navigate("/lawyer-profile");
          }
        }
      }

      if (userProfile.role === "client") {
        console.log("👤 User is a client, checking profile completion...");
        console.log("🔍 Client profile data:", userProfile);
        
        // Check if client has essential profile data
        const hasEssentialData = userProfile.full_name || userProfile.fullName || userProfile.name;
        const isProfileComplete = userProfile.profile_complete || hasEssentialData;
        
        console.log("🔍 Client profile analysis:", {
          hasEssentialData,
          profile_complete: userProfile.profile_complete,
          isProfileComplete
        });
        
        if (isProfileComplete) {
          console.log("✅ Client complete → dashboard");
          return navigate("/dashboard");
        } else {
          console.log("📝 Client incomplete → profile");
          return navigate("/profile");
        }
      }

      // fallback
      return navigate("/role-selection");

    } catch (err) {
      console.error("❌ Navigation error:", err);
      return navigate("/role-selection");
    }
  };
  
  console.log('✅ AuthPage: Auth loaded, rendering component');

  const translations = {
    english: {
      welcomeBack: 'Welcome Back',
      signInAccount: 'Sign in to your account',
      continueWithGoogle: 'Continue with Google',
      or: 'or',
      email: 'Email',
      fullName: 'Full Name',
      enterEmail: 'Enter your email',
      enterPassword: 'Enter your password',
      enterFullName: 'Enter your full name',
      signIn: 'Sign In',
      createAccount: 'Create Account',
      noAccount: 'Don\'t have an account?',
      registerHere: 'Register here',
      hasAccount: 'Already have an account?',
      signInHere: 'Sign in here',
      forgotPassword: 'Forgot Password?',
      resetPassword: 'Reset Password',
      resetPasswordDesc: 'Enter your email address and we\'ll send you a link to reset your password.',
      enterEmailForReset: 'Enter your email address',
      sendResetLink: 'Send Reset Link',
      backToSignIn: 'Back to Sign In',
      resetEmailSent: 'Reset email sent!',
      resetEmailSentDesc: 'We\'ve sent a password reset link to your email address. Please check your inbox and follow the instructions.',
      checkEmail: 'Check your email',
      didntReceiveEmail: 'Didn\'t receive the email?',
      tryAgain: 'Try again',
      close: 'Close'
    },
    hindi: {
      welcomeBack: 'वापसी पर स्वागत है',
      signInAccount: 'अपने खाते में साइन इन करें',
      continueWithGoogle: 'Google के साथ जारी रखें',
      or: 'या',
      email: 'ईमेल',
      fullName: 'पूरा नाम',
      enterEmail: 'अपना ईमेल दर्ज करें',
      enterPassword: 'अपना पासवर्ड दर्ज करें',
      enterFullName: 'अपना पूरा नाम दर्ज करें',
      signIn: 'साइन इन करें',
      createAccount: 'खाता बनाएं',
      noAccount: 'खाता नहीं है?',
      registerHere: 'यहां पंजीकरण करें',
      hasAccount: 'पहले से खाता है?',
      signInHere: 'यहां साइन इन करें',
      forgotPassword: 'पासवर्ड भूल गए?',
      resetPassword: 'पासवर्ड रीसेट करें',
      resetPasswordDesc: 'अपना ईमेल पता दर्ज करें और हम आपको पासवर्ड रीसेट करने का लिंक भेजेंगे।',
      enterEmailForReset: 'अपना ईमेल पता दर्ज करें',
      sendResetLink: 'रीसेट लिंक भेजें',
      backToSignIn: 'साइन इन पर वापस जाएं',
      resetEmailSent: 'रीसेट ईमेल भेज दिया गया!',
      resetEmailSentDesc: 'हमने आपके ईमेल पते पर पासवर्ड रीसेट लिंक भेज दिया है। कृपया अपना इनबॉक्स देखें और निर्देशों का पालन करें।',
      checkEmail: 'अपना ईमेल देखें',
      didntReceiveEmail: 'ईमेल नहीं मिला?',
      tryAgain: 'फिर से कोशिश करें',
      close: 'बंद करें'
    }
  };

  const t = translations[language] || translations.english;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      console.log('🔐 Starting email sign-in process...');
      const result = await signIn(formData.email, formData.password);
      if (result.success) {
        console.log('✅ Email sign-in successful');
        setJustLoggedIn(true);
        toast.success('Sign in successful!');
        console.log('🎯 Sign-in complete, triggering navigation with user:', result.user?.uid);
        
        // Use the user from the sign-in result directly
        await handlePostAuthNavigation(result.user);
      } else {
        console.error('❌ Email sign-in failed:', result.error);
        toast.error(result.error || 'Sign in failed');
      }
    } catch (error) {
      console.error('❌ Email sign-in error:', error);
      toast.error('Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await signUp(formData.email, formData.password, formData.fullName);
      if (result.success) {
        console.log('✅ Email sign-up successful');
        setJustLoggedIn(true);
        toast.success('Account created successfully!');
        console.log('🎯 Sign-up complete, triggering navigation with user:', result.user?.uid);
        
        // Use the user from the sign-up result directly
        await handlePostAuthNavigation(result.user);
      } else {
        console.error('❌ Email sign-up failed:', result.error);
        
        // Handle specific error cases
        if (result.error.includes('auth/email-already-in-use')) {
          toast.error('This email is already registered. Please sign in instead.');
          setAuthMethod('signin');
          setFormData(prev => ({ ...prev, password: '' })); // Clear password
        } else {
          toast.error(result.error || 'Sign up failed');
        }
      }
    } catch (error) {
      console.error('❌ Email sign-up error:', error);
      
      // Handle specific error cases
      if (error.message.includes('auth/email-already-in-use')) {
        toast.error('This email is already registered. Please sign in instead.');
        setAuthMethod('signin');
        setFormData(prev => ({ ...prev, password: '' })); // Clear password
      } else {
        toast.error('Sign up failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    
    try {
      console.log('🔐 Starting Google sign-in process...');
      const result = await signInWithGoogle();
      if (result.success) {
        console.log('✅ Google sign-in successful');
        setJustLoggedIn(true);
        toast.success('Sign in successful!');
        console.log('🎯 Sign-in complete, triggering navigation with user:', result.user?.uid);
        
        // Use the user from the sign-in result directly
        await handlePostAuthNavigation(result.user);
      } else {
        console.error('❌ Google sign-in failed:', result.error);
        
        // If popup fails, try redirect method
        if (result.error.includes('popup') || result.error.includes('blocked')) {
          console.log('🔄 Popup failed, trying redirect method...');
          toast.info('Popup blocked. Trying redirect method...');
          
          const redirectResult = await signInWithGoogleRedirect();
          if (redirectResult.success) {
            toast.success('Redirecting to Google...');
          } else {
            toast.error('Google sign-in failed. Please try again.');
          }
        } else {
          toast.error(result.error || 'Google sign in failed');
        }
      }
    } catch (error) {
      console.error('❌ Google sign-in error:', error);
      
      // Handle specific Google sign-in errors
      let errorMessage = 'Google sign in failed. Please try again.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Pop-up was blocked. Trying redirect method...';
        // Try redirect method
        try {
          await signInWithGoogleRedirect();
          toast.info('Redirecting to Google...');
          return;
        } catch (redirectError) {
          errorMessage = 'Google sign-in failed. Please try again.';
        }
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Google sign-in is not enabled. Please contact support.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsResetting(true);
    
    try {
      const result = await resetPassword(resetEmail);
      if (result.success) {
        setResetSent(true);
        toast.success('Password reset email sent!');
      } else {
        toast.error(result.error || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('❌ Reset password error:', error);
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  // Show loading screen when redirecting
  if (isRedirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Redirecting...</h2>
            <p className="text-gray-600">Taking you to your dashboard</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={logoSvg} alt="Logo" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">{t.welcomeBack}</h1>
          <p className="text-gray-600">{t.signInAccount}</p>
        </div>

        {/* Auth Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-6"
        >
          {/* Continue as existing user option */}
          {isAuthenticated && currentUser?.email && !justLoggedIn && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="text-center">
                <p className="text-sm text-green-700 mb-3">
                  You're already signed in as:
                </p>
                <p className="font-medium text-green-800 mb-4">
                  {currentUser.email}
                </p>
                <button
                  onClick={() => {
                    console.log('🎯 Continue as existing user clicked');
                    setJustLoggedIn(true);
                    setIsRedirecting(true);
                    
                    // Use current user for navigation
                    handlePostAuthNavigation(currentUser);
                  }}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-green-700 transition-colors"
                >
                  Continue as {currentUser.email}
                </button>
                <button
                  onClick={() => {
                    console.log('🔄 Sign out and start fresh');
                    // Sign out and clear state
                    firebaseLogout();
                    setJustLoggedIn(false);
                    localStorage.removeItem('signInSuccessful');
                  }}
                  className="w-full mt-2 text-sm text-green-600 hover:text-green-700 underline"
                >
                  Sign out and use different account
                </button>
              </div>
            </div>
          )}

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 mb-6"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {isLoading ? 'Signing in...' : t.continueWithGoogle}
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t.or}</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={authMethod === 'signin' ? handleEmailSignIn : handleEmailSignUp}>
            {authMethod === 'signup' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.fullName}
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required={authMethod === 'signup'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={t.enterFullName}
                />
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.email}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t.enterEmail}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                  placeholder={t.enterPassword}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
            </div>

            {authMethod === 'signin' && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => setShowResetModal(true)}
                  className="text-sm text-purple-600 hover:text-purple-500"
                >
                  {t.forgotPassword}
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {authMethod === 'signin' ? 'Signing in...' : 'Creating account...'}
                </div>
              ) : (
                authMethod === 'signin' ? t.signIn : t.createAccount
              )}
            </button>
          </form>

          {/* Toggle Auth Method */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {authMethod === 'signin' ? t.noAccount : t.hasAccount}
              <button
                onClick={() => setAuthMethod(authMethod === 'signin' ? 'signup' : 'signin')}
                className="ml-1 text-purple-600 hover:text-purple-500 font-medium"
              >
                {authMethod === 'signin' ? t.registerHere : t.signInHere}
              </button>
            </p>
          </div>
        </motion.div>

        {/* Reset Password Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t.resetPassword}</h3>
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setResetEmail('');
                    setResetSent(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {!resetSent ? (
                <>
                  <p className="text-gray-600 mb-4">{t.resetPasswordDesc}</p>
                  <form onSubmit={handleResetPassword}>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-4"
                      placeholder={t.enterEmailForReset}
                    />
                    <button
                      type="submit"
                      disabled={isResetting}
                      className="w-full bg-purple-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                      {isResetting ? 'Sending...' : t.sendResetLink}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center">
                  <div className="text-green-500 text-4xl mb-4">✓</div>
                  <h4 className="text-lg font-semibold mb-2">{t.resetEmailSent}</h4>
                  <p className="text-gray-600 mb-4">{t.resetEmailSentDesc}</p>
                  <button
                    onClick={() => {
                      setShowResetModal(false);
                      setResetEmail('');
                      setResetSent(false);
                    }}
                    className="w-full bg-purple-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-purple-700 transition-colors"
                  >
                    {t.close}
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
